import React from 'react';
import {
  Package, Users, TrendingUp, FileText, Database,
  Shield, Clock, BarChart3,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const SparesVendorsManagementPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Spares & Vendors',
      title: (
        <>
          Intelligent spares &amp; vendor <em>management platform</em>
        </>
      ),
      lede: 'Streamline your spare parts and vendor management with our comprehensive platform. Optimize inventory, track vendor performance, and reduce costs while ensuring parts availability for renewable energy assets.',
      image: '/assets/img/general/wind-power-generation-8370867_1280.jpg',
      imageAlt: 'Wind power generation site',
      actions: [
        { label: 'Request a walkthrough', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '30%', label: 'Cost reduction' },
        { value: '24/7', label: 'Inventory tracking' },
        { value: 'Real-time', label: 'Vendor insights' },
        { value: '100%', label: 'Parts visibility' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'A complete management solution',
      lede: 'Our spares and vendor management platform provides comprehensive tools to optimize inventory, track vendors, and streamline procurement.',
      body: [
        'Stock levels, purchase orders, supplier scorecards and maintenance demand sit in one system, so a critical part shortage surfaces as a procurement action rather than as an outage.',
      ],
      checklist: [
        'Centralized spare parts inventory management',
        'Vendor performance tracking and evaluation',
        'Automated procurement workflows',
        'Stock level optimization and alerts',
        'Supplier relationship management',
        'Cost analysis and budget tracking',
        'Quality control and compliance management',
        'Integration with maintenance systems',
      ],
      image: '/assets/img/general/windmills-1838788_1280.jpg',
      imageAlt: 'Wind turbines requiring scheduled maintenance',
      imageCaption: 'Streamline spares and vendor management with intelligent automation and insights',
    }}
    capabilities={{
      eyebrow: 'Platform features',
      title: 'Comprehensive management tools',
      lede: 'Complete solution for spares and vendor management.',
      items: [
        {
          icon: Package,
          title: 'Spares management',
          description: 'Comprehensive spare parts inventory management with automated tracking and optimization.',
        },
        {
          icon: Users,
          title: 'Vendor management',
          description: 'Centralized vendor database with performance tracking and relationship management.',
        },
        {
          icon: Database,
          title: 'Inventory optimization',
          description: 'Smart inventory optimization to reduce costs while ensuring parts availability.',
        },
        {
          icon: FileText,
          title: 'Procurement management',
          description: 'Streamlined procurement processes with automated workflows and approvals.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'Management areas',
      title: 'Complete management coverage',
      lede: 'Manage all aspects of spares and vendor operations.',
      items: [
        {
          icon: Package,
          title: 'Inventory management',
          description: 'Track and optimize spare parts inventory with real-time visibility and automated alerts.',
        },
        {
          icon: BarChart3,
          title: 'Vendor performance',
          description: 'Monitor vendor performance metrics including delivery time, quality, and pricing.',
        },
        {
          icon: FileText,
          title: 'Procurement process',
          description: 'Streamline procurement with automated workflows, approvals, and purchase orders.',
        },
        {
          icon: TrendingUp,
          title: 'Cost optimization',
          description: 'Analyze spending patterns and optimize costs across spares and vendors.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Key benefits',
      title: 'Transform your operations',
      lede: 'Achieve superior results through effective management.',
      items: [
        {
          icon: TrendingUp,
          title: 'Reduce costs',
          description: 'Lower inventory and procurement costs by 20-30% through optimization.',
        },
        {
          icon: Clock,
          title: 'Minimize downtime',
          description: 'Ensure critical spares availability to reduce equipment downtime.',
        },
        {
          icon: Shield,
          title: 'Quality assurance',
          description: 'Maintain quality standards with verified vendors and certified parts.',
        },
        {
          icon: BarChart3,
          title: 'Better planning',
          description: 'Data-driven insights for improved procurement and inventory planning.',
        },
      ],
    }}
    cta={{
      title: 'Ready to optimize management?',
      body: 'Start streamlining your spares and vendor management today.',
      primary: { label: 'Talk to our team', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default SparesVendorsManagementPage;
